import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { PropsWithChildren } from 'react'

import { Ticket } from '@types'

import InfoField from '../components/InfoField'
import InfoSection from '../components/InfoSection'
import { DATE_KEYS } from '../../../../constants/dateKeys'

interface DataSectionProps {
  ticket: Ticket
}

const DataSection: React.FC<PropsWithChildren<DataSectionProps>> = ({ ticket, children }) => {
  const { t } = useTranslation()

  const formatDate = (date?: number | string) => {
    if (date) {
      return dayjs(date).format(DATE_KEYS.FORMAT.FULL_TIME)
    }
    return t('data_section.pending')
  }

  const truckInfo = ticket.truck?.equipmentType
    ? `${ticket.truck?.licensePlate} - ${ticket.truck?.description} - ${ticket.truck?.equipmentType?.nameShort}`
    : `${ticket.truck?.licensePlate} - ${ticket.truck?.description}`

  const trailerInfo = `${ticket.trailer?.licensePlate} - ${ticket.trailer?.description} - ${ticket.trailer?.equipmentType?.nameShort}`

  return (
    <InfoSection
      header={t('data_section.date_info')}
      style={{ minWidth: '22rem', maxWidth: '100%', flex: 2 }}
    >
      <InfoField label={t('data_section.origin')}>{ticket.origin.tag}</InfoField>
      <InfoField label={t('data_section.destination')}>{ticket.destination.tag}</InfoField>
      <InfoField label={t('data_section.purchase_order')}>
        {ticket.documentNumberId ?? '-'}
      </InfoField>
      <InfoField label={t('data_section.material')}>{ticket.material.nameShort}</InfoField>
      <InfoField label={t('data_section.equipment')}>{ticket.equipment.name}</InfoField>
      <InfoField label={t('data_section.created_date')}>
        {dayjs(ticket.createAt).format(DATE_KEYS.FORMAT.FULL_TIME)}
      </InfoField>
      <InfoField label={t('data_section.driver')}>
        {ticket.driver ? (
          <span className="ticket-field-value">
            {ticket.driver?.stateIdNumber}
            {' - '}
            {ticket.driver?.firstName} {ticket.driver?.lastName}
          </span>
        ) : (
          <span style={{ color: 'red' }}>N/A</span>
        )}
      </InfoField>
      <InfoField label={t('data_section.truck')}>
        {ticket.truck ? (
          <span title={truckInfo} className="ticket-field-value">
            {truckInfo.length > 50 ? `${truckInfo.slice(0, 50)}...` : truckInfo}
          </span>
        ) : (
          <span style={{ color: 'red' }}>N/A</span>
        )}
      </InfoField>
      <InfoField label={t('data_section.trailer')}>
        {ticket.trailer ? (
          <span title={trailerInfo} className="ticket-field-value">
            {trailerInfo.length > 50 ? `${trailerInfo.slice(0, 50)}...` : trailerInfo}
          </span>
        ) : (
          <span style={{ color: 'red' }}>N/A</span>
        )}
      </InfoField>
      <InfoField label={t('data_section.load_date')}>{formatDate(ticket.tripStartDate)}</InfoField>
      <InfoField label={t('data_section.load_distance_to_origin')}>
        {ticket.distanceTripStartDepot ? `${ticket.distanceTripStartDepot} m` : '-'}
      </InfoField>
      <InfoField label={t('data_section.unload_date')}>{formatDate(ticket.tripEndDate)}</InfoField>
      <InfoField label={t('data_section.textract')}>{ticket.textractData}</InfoField>
      {children}
    </InfoSection>
  )
}

export default DataSection
